import React, { useState, useMemo, useEffect } from 'react';
import { doc, updateDoc, arrayUnion, collection, getDocs, getDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import SheetDetailModal from './SheetDetailModal';
import LogModal from './Modals/LogModal';
import ReportFailureModal from './Modals/ReportFailureModal';
import { Workflow, Eye, FileText, X, ArrowUpDown, AlertTriangle, Printer, Scissors, Search, Sparkles, CornerUpLeft } from 'lucide-react';
import { getSheetDisplayStatus, buildAlertTaskBySheet, isCorregidaPorDiseno, isSheetUpdated, PREPRESS_STATUS_WHITELIST, SHEET_STATUS_OPTIONS } from '../../../utils/sheetStatus';
import { isTwoSided, computeProductionStatus, printedBack as printedBackOf, printedDone, productionResetFields } from '../../../lib/production';
import { previewThumb } from '../../../lib/previews';
import { fileSlots, readinessLevel, matchesFileFilter, tallyReadiness } from '../../../lib/productionFiles';
import { buildTaskAssignment, buildAssignmentEmail, buildAssignedByStamp } from '../../../utils/taskAssignment';

// --- Helper functions ---
// B1 (D23): avance desde prepress por la callable. machineId = el de la ficha de
// la estación (`J-x__IMPRESION` para print/printBack, `J-x__CORTE` para cut) si
// existe y se puede leer; si no, null. Exportado para SheetDetailModal.
export async function recordPrepressAdvance(db, { jobId, sheetId, type, delta }) {
    let machineId = null;
    try {
        const station = type === 'cut' ? 'CORTE' : 'IMPRESION';
        const ficha = await getDoc(doc(db, 'work_queue', `${jobId}__${station}`));
        machineId = (ficha.exists() && ficha.data().machineId) || null;
    } catch (_) { /* sin permiso de cola o sin ficha: se registra sin máquina */ }
    const fn = httpsCallable(getFunctions(getApp(), 'us-central1'), 'recordProductionScan');
    const res = await fn({ jobId, sheetId, type, delta, machineId, source: 'prepress' });
    return res?.data || {};
}

const generateSheetFileName = (sheet, jobData) => {
    if (!jobData || !sheet || !sheet.block || !sheet.sheetNumber) return sheet.hri || 'N/A';
    const jobNumber = jobData.id.replace('J-', '');
    const blockObject = jobData.blocks?.find(b => b.name === sheet.block);
    const blockCode = blockObject?.code || sheet.block.replace(/\s+/g, '_').toUpperCase();
    const baseName = `${jobNumber}-${blockCode}_${String(sheet.sheetNumber).padStart(3, '0')}`;
    if (!sheet.isPrinting) return `${baseName}N`;
    if (sheet.printFaces == 1) return `${baseName}P`;
    if (sheet.printFaces == 2) return `${baseName}A/B`;
    return sheet.hri;
};

const getPrintType = (sheet) => {
    if (!sheet.isPrinting) return 'Solo Corte';
    return sheet.printFaces == 2 ? '2 Caras' : '1 Cara';
};

// NOTA: getSheetDisplayStatus ahora vive en src/utils/sheetStatus.js (fuente
// única de verdad). Una plancha en ALERTA nunca se mostrará como "Producido".


// --- Sub-Componentes (Modales y UI) ---
const ImageViewerModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex justify-center items-center p-4" onClick={onClose}>
            <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
                <img src={imageUrl} alt="Vista ampliada" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
                <button onClick={onClose} className="absolute -top-2 -right-2 bg-white text-black rounded-full h-8 w-8 flex items-center justify-center shadow-lg">&times;</button>
            </div>
        </div>
    );
};

// ReportFailureModal se movió a ./Modals/ReportFailureModal.js (compartido con
// SheetDetailModal). Incluye asignación obligatoria a usuario + sugerencia IA.

const StatusBadge = ({ status }) => {
    const statusStyles = {
        'Listo para Prepress': 'bg-blue-100 text-blue-800', 'Pendiente': 'bg-blue-100 text-blue-800',
        'Corregido por Diseño': 'bg-purple-100 text-purple-800', 'Devuelto a Diseño': 'bg-red-100 text-red-800',
        'Reproducir': 'bg-orange-100 text-orange-800',
        'En Hold': 'bg-yellow-100 text-yellow-800', 'Listo para Producción': 'bg-green-100 text-green-800',
        'Impresa': 'bg-yellow-200 text-yellow-800',
        'Cortada': 'bg-green-200 text-green-800',
        'Impresa y Cortada': 'bg-green-200 text-green-800',
        'Producido': 'bg-green-500 text-white',
        'En Ruta': 'bg-blue-100 text-blue-800',           // legacy → equivale a Listo para Prepress
        'Devuelto a Revisión': 'bg-red-100 text-red-800', // legacy → equivale a Devuelto a Diseño
        'default': 'bg-gray-100 text-gray-800'
    };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusStyles[status] || statusStyles.default}`}>{status}</span>;
};

// La fila parpadea en el MISMO color que el estado (atención): rojo=devuelta,
// naranja=reproducir, amarillo=hold. Otros estados no parpadean.
// Las claves son ESTADOS MOSTRADOS, no crudos: los legados ('Devuelto a Revisión',
// 'Hold') ya los normaliza getSheetDisplayStatus, y el morado de "corregida" salió
// de acá porque ya no es un estado — lo pinta isCorregidaPorDiseno en la fila.
const STATUS_BLINK_ROW = {
    'Devuelto a Diseño': 'bg-red-100 animate-pulse',
    'Reproducir': 'bg-orange-100 animate-pulse',
    'En Hold': 'bg-yellow-100 animate-pulse',
};

const AiAssistantModal = ({ isOpen, onClose, sheetsData, jobData }) => {
    const [query, setQuery] = useState('');
    const [response, setResponse] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const prepareDataForAI = (sheets) => {
        return sheets.map(s => {
            const history = (s.history || []).map(h => ({
                action: h.action,
                user: h.user,
                timestamp: h.timestamp?.toDate ? h.timestamp.toDate().toISOString() : 'N/A',
                details: h.details || ''
            }));

            const creationEvent = (s.history || []).find(h => h.action === 'Plancha Creada');
            const lastEvent = (s.history || []).slice(-1)[0];

            return {
                nombre_plancha: generateSheetFileName(s, jobData),
                estado_actual: getSheetDisplayStatus(s),
                repeticiones_totales: s.repetitions,
                progreso_produccion: {
                    impresas: s.printedCount || 0,
                    cortadas: s.cutCount || 0,
                },
                fecha_creacion: creationEvent?.timestamp?.toDate ? creationEvent.timestamp.toDate().toISOString() : 'No registrada',
                ultima_actualizacion: lastEvent?.timestamp?.toDate ? lastEvent.timestamp.toDate().toISOString() : 'No registrada',
                historial_eventos: history,
            };
        });
    };

    const handleQuery = async () => {
        if (!query.trim()) return;
        setIsLoading(true);
        setResponse('');
        
        const preparedData = prepareDataForAI(sheetsData);
        
        const context = `Contexto: Eres un auditor de producción experto para el trabajo "${jobData.jobName}". Tu tarea es analizar el siguiente JSON que representa el estado de varias planchas de producción y responder a la pregunta del usuario basándote únicamente en estos datos. Para determinar la fecha de creación de una plancha, utiliza el campo "fecha_creacion". La fecha actual para referencia es ${new Date().toLocaleDateString()}. Datos de Planchas: ${JSON.stringify(preparedData, null, 2)}`;
        
        const fullPrompt = `${context}\n\nPregunta: ${query}`;

        try {
            const functions = getFunctions(getApp(), 'us-central1');
            const callPrepressAi = httpsCallable(functions, 'callPrepressAi');
            const result = await callPrepressAi({ text: fullPrompt });
            const aiText = result.data?.suggestion || "No se pudo obtener una respuesta.";
            setResponse(aiText);
        } catch (error) {
            console.error("Error with AI Assistant:", error);
            setResponse(`Error al contactar la IA. ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[80vh]">
                <div className="p-4 border-b flex justify-between items-center"><h2 className="text-lg font-bold flex items-center gap-2"><Sparkles className="text-purple-500"/> Asistente de IA de Prepress</h2><button onClick={onClose}>&times;</button></div>
                <div className="p-6 flex-grow overflow-y-auto"><div className="prose max-w-none text-sm whitespace-pre-wrap">{isLoading ? "Analizando..." : (response || "Hola, pregunta lo que necesites saber sobre el estado de estas planchas.")}</div></div>
                <div className="p-4 border-t flex items-center gap-2">
                    <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleQuery()} placeholder="Ej: ¿Qué planchas fueron creadas hoy?" className="flex-grow p-2 border rounded-md" />
                    <button onClick={handleQuery} disabled={isLoading} className="px-4 py-2 bg-purple-600 text-white font-bold rounded-md disabled:bg-gray-400">Enviar</button>
                </div>
            </div>
        </div>
    );
};

const EditableNote = ({ sheetId, initialNote, db, jobId, user }) => {
    const [note, setNote] = useState(initialNote || '');
    const [isEditing, setIsEditing] = useState(false);
    
    useEffect(() => { 
        setNote(initialNote || ''); 
    }, [initialNote]);

    const handleSave = async () => {
        const trimmedNote = note.trim();
        const fieldToUpdate = 'prepressNotes';

        if (trimmedNote !== (initialNote || '').trim()) {
            const sheetRef = doc(db, 'jobs', jobId, 'sheets', sheetId);
            const historyEntry = {
                action: 'Nota de Prepress actualizada',
                details: `La nota fue cambiada a: "${trimmedNote}"`,
                user: user?.email || 'sistema',
                timestamp: new Date()
            };
            try {
                await updateDoc(sheetRef, { 
                    [fieldToUpdate]: trimmedNote, 
                    history: arrayUnion(historyEntry) 
                });
            } catch (error) {
                console.error("Error al guardar la nota:", error);
            }
        }
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <textarea 
                value={note} 
                onChange={(e) => setNote(e.target.value)} 
                className="w-full p-1.5 border rounded-md text-xs bg-white shadow-inner text-center" 
                rows="3" 
                autoFocus 
                onBlur={handleSave}
            />
        );
    }
    
    return (
        <div 
            className="text-xs text-gray-700 whitespace-pre-wrap min-h-[40px] p-1.5 rounded-md cursor-pointer hover:bg-gray-100 text-center" 
            onDoubleClick={() => setIsEditing(true)}
            title="Doble clic para editar la nota"
        >
            {note || <span className="text-gray-400 italic">Doble clic para añadir nota...</span>}
        </div>
    );
};


// Indicadores de archivos de producción. Responden "¿qué falta para producir?" sin
// abrir el modal de cada plancha. Los estados los deriva src/lib/productionFiles.js
// de la regla medida sobre las 1301 planchas producidas; acá solo se pintan.
//   ok       verde     · el archivo está
//   falta    ámbar     · requerido y ausente, pero la plancha sigue en prepress
//   falta    ROJO      · requerido y ausente con la plancha ya "Listo para Producción"
//   opcional gris      · ausente, pero el grupo de corte ya se cumple con otro archivo
//   na       atenuado  · no aplica (PRINT en solo corte, DORSO en 1 cara). NUNCA rojo.
const SLOT_STYLE = {
    ok: 'bg-green-100 text-green-800 border-green-200',
    opcional: 'bg-gray-50 text-gray-400 border-gray-200',
    na: 'bg-transparent text-gray-300 border-transparent',
};
const FALTA_STYLE = {
    error: 'bg-red-100 text-red-800 border-red-300 font-bold',
    falta: 'bg-amber-100 text-amber-800 border-amber-300',
};
const SLOT_TITLE = {
    ok: 'Archivo cargado',
    opcional: 'No está, pero el corte ya tiene otro archivo',
    na: 'No aplica para este tipo de plancha',
};

const FileIndicators = ({ sheet }) => {
    const slots = fileSlots(sheet);
    const nivel = readinessLevel(sheet);   // 'listo' | 'falta' | 'error'
    return (
        <div className="flex flex-col items-center gap-1">
            <div className="flex items-center justify-center gap-0.5 flex-wrap">
                {slots.filter(s => s.estado !== 'na').map(({ key, estado }) => (
                    <span
                        key={key}
                        title={estado === 'falta'
                            ? (nivel === 'error' ? `Falta ${key} y la plancha ya está en Producción` : `Falta ${key}`)
                            : `${key}: ${SLOT_TITLE[estado]}`}
                        className={`px-1 py-0.5 text-[10px] leading-none font-mono rounded border ${estado === 'falta' ? FALTA_STYLE[nivel === 'error' ? 'error' : 'falta'] : SLOT_STYLE[estado]}`}
                    >
                        {key}
                    </span>
                ))}
            </div>
            {nivel !== 'listo' && (
                <span className={`text-[10px] font-semibold ${nivel === 'error' ? 'text-red-700' : 'text-amber-700'}`}>
                    {nivel === 'error' ? '⚠ sin archivos' : 'incompleta'}
                </span>
            )}
        </div>
    );
};

const PrepressTableRow = ({ sheet, db, jobData, materialsCatalog, onSelectSheetForDetails, onSelectSheetForLog, user, setViewerImage, users = [], alertTask = null }) => {
    const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);
    const [failureModalType, setFailureModalType] = useState('production'); // 'production' | 'design'

    const hasPendingTask = !!alertTask;

    const materialName = materialsCatalog.find(m => m.id === sheet.materialId)?.name || 'N/A';
    const printedCount = sheet.printedCount || 0;
    const printedCountBack = printedBackOf(sheet);
    const cutCount = sheet.cutCount || 0;
    const repetitions = sheet.repetitions || 1;
    const hasCut = sheet.hasCut !== false; // legado: si no existe el flag, se asume con corte
    const twoSided = isTwoSided(sheet);     // impresión 2 caras → frente + dorso por separado

    // Estado/color derivados del criterio compartido (frente+dorso completos para 2 caras).
    const getProductionStatusAndColor = (s) => {
        const st = computeProductionStatus(s);
        if (['Producido', 'Cortada', 'Impresa y Cortada'].includes(st)) return { statusText: st, rowClass: 'bg-green-100' };
        if (st === 'Impresa') return { statusText: 'Impresa', rowClass: 'bg-yellow-100' };
        return { statusText: null, rowClass: 'odd:bg-slate-50' };
    };

    const productionInfo = getProductionStatusAndColor(sheet);

    // Los dos ejes, calculados una sola vez: el ESTADO (excluyente) y la señal
    // ortogonal de "volvió corregida de diseño".
    const displayStatus = getSheetDisplayStatus(sheet, alertTask);
    const esCorregida = isCorregidaPorDiseno(sheet);

    // type: 'print' (frente) | 'printBack' (dorso) | 'cut'. El corte exige impresión completa.
    // B1 (D23): ya no escribe contadores directo (las rules lo deniegan). Llama a
    // recordProductionScan con source 'prepress', delta = diferencia contra el valor
    // actual y machineId = el de la ficha de la estación si existe. Misma UX.
    const handleProductionUpdate = async (type) => {
        const fieldToUpdate = type === 'print' ? 'printedCount' : type === 'printBack' ? 'printedCountBack' : 'cutCount';
        const currentCount = sheet[fieldToUpdate] || 0;
        if (currentCount >= repetitions) return;
        const newCount = currentCount + 1;
        try {
            await recordPrepressAdvance(db, { jobId: jobData.id, sheetId: sheet.id, type, delta: newCount - currentCount });
        } catch (e) {
            console.error('recordProductionScan (prepress) falló:', e);
            alert(`No se pudo registrar la marca: ${e?.message || 'error'}`);
        }
    };

    // Crea la alerta (tarea de sistema), asigna a un usuario real, persiste el
    // estado coherente en la plancha y dispara UN correo al asignado.
    const handleReportFailure = async ({ failureType, failedCount, reason, assignedUser, aiCleanedDescription, aiSuggestedAction }) => {
        const sheetName = generateSheetFileName(sheet, jobData);
        const sheetRef = doc(db, 'jobs', jobData.id, 'sheets', sheet.id);
        const batch = writeBatch(db);

        // Descripción final: la mejorada por IA si existe, si no la original.
        const finalDescription = aiCleanedDescription || reason;
        const isDesign = failureType === 'design';

        const baseTask = {
            jobId: jobData.id,
            sheetId: sheet.id,
            relatedPlateId: sheet.id,
            status: 'pending',
            createdBy: user.email,
            createdByName: user.displayName || user.email,
            createdAt: serverTimestamp(),
            // Texto original + sugerencia de IA (la IA solo asiste, no decide).
            originalDescription: reason,
            aiSuggestedAction: aiSuggestedAction || null,
            ...buildTaskAssignment(assignedUser),
            ...buildAssignedByStamp(user),
        };

        let taskData;
        if (isDesign) {
            // Error de diseño → Devuelto a Diseño + tarea de corrección.
            batch.update(sheetRef, {
                status: 'Devuelto a Diseño',
                hasActiveAlert: true,
                history: arrayUnion({
                    action: 'Devuelto a Diseño desde Prepress',
                    details: `Motivo: ${finalDescription}`,
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
            taskData = {
                ...baseTask,
                type: 'system',
                title: `Corrección: Plancha ${sheetName}`,
                description: finalDescription,
            };
        } else {
            // Falla de producción → tarea de reproducción. El estado mostrado pasa a "Reproducir"
            // automáticamente vía la tarea de alerta (getSheetDisplayStatus), y se auto-limpia al
            // completar la tarea — por eso NO se sobreescribe sheet.status (evita que quede pegado).
            // Los contadores se reinician AQUÍ, al reportar: el ciclo de reproducción empieza en
            // este instante, y dejarlos topados bloquea los controles durante toda la ventana.
            batch.update(sheetRef, {
                ...productionResetFields(),
                hasActiveAlert: true,
                history: arrayUnion({
                    action: 'Falla de Producción Reportada',
                    details: `${failedCount} plancha(s) dañada(s). Razón: ${finalDescription}`,
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
            taskData = {
                ...baseTask,
                type: 'system_reproduction',
                title: `Reproducción: ${failedCount}x Plancha ${sheetName}`,
                description: `Se solicitaron ${failedCount} planchas de reposición. Causa: ${finalDescription}`,
            };
        }

        batch.set(doc(collection(db, 'jobs', jobData.id, 'tasks')), taskData);

        // UN solo correo al usuario asignado (vía extensión Trigger Email).
        const mail = buildAssignmentEmail({
            assignedUser,
            assignedBy: user,
            taskTitle: taskData.title,
            taskDescription: taskData.description,
            jobId: jobData.id,
            suggestedAction: aiSuggestedAction,
        });
        if (mail) {
            batch.set(doc(collection(db, 'mail')), mail);
        }

        await batch.commit();
    };

    return (
        <>
            <ReportFailureModal
                isOpen={isFailureModalOpen}
                onClose={() => setIsFailureModalOpen(false)}
                onSubmit={handleReportFailure}
                sheetName={generateSheetFileName(sheet, jobData)}
                users={users}
                initialFailureType={failureModalType}
                sheetContext={`Material: ${materialName} | ${getPrintType(sheet)} | Reps: ${repetitions}`}
            />
            <tr className={`hover:bg-gray-100 ${STATUS_BLINK_ROW[displayStatus] || (esCorregida && 'bg-purple-100 animate-pulse') || (hasPendingTask ? 'bg-red-100 animate-pulse' : productionInfo.rowClass)}`}>
                <td className="px-2 py-2 text-center align-middle">
                    <div className="flex items-center justify-center gap-1">
                        {/* La miniatura pinta la variante (200px, ~7KB); el clic abre SIEMPRE el original. */}
                        <img src={previewThumb(sheet, 'front') || 'https://placehold.co/80x50/eee/ccc?text=N/A'} onClick={() => setViewerImage(sheet.previewImageFrontUrl)} className="w-14 h-auto object-contain rounded shadow-sm cursor-pointer hover:scale-110 transition-transform" alt="Frente" title="Frente"/>
                        {twoSided && sheet.previewImageBackUrl && <img src={previewThumb(sheet, 'back')} onClick={() => setViewerImage(sheet.previewImageBackUrl)} className="w-14 h-auto object-contain rounded shadow-sm cursor-pointer hover:scale-110 transition-transform" alt="Dorso" title="Dorso"/>}
                    </div>
                </td>
                <td className="px-2 py-2 text-center align-middle font-mono">{generateSheetFileName(sheet, jobData)}</td>
                <td className="px-2 py-2 align-middle">{materialName}</td>
                <td className="px-2 py-2 text-center align-middle">{getPrintType(sheet)}</td>
                <td className="px-2 py-2 text-center align-middle">{repetitions}</td>
                <td className="px-2 py-2 align-middle text-center">
                    <EditableNote 
                        sheetId={sheet.id} 
                        initialNote={sheet.prepressNotes}
                        db={db} 
                        jobId={jobData.id} 
                        user={user}
                    />
                </td>
                <td className="px-2 py-2 align-middle">
                    <FileIndicators sheet={sheet} />
                </td>
                <td className="px-2 py-2 text-center align-middle">
                    <div className="flex flex-col items-center gap-1">
                        <StatusBadge status={displayStatus} />
                        {/* "Corregida" no es un estado (la plancha está Lista para Prepress),
                            pero la señal de que volvió de diseño tiene que verse igual. */}
                        {esCorregida && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-800" title="Volvió corregida de Diseño">↻ Corregida</span>
                        )}
                    </div>
                </td>
                <td className="px-2 py-2 text-center align-middle">
                    {/* AVANCE de producción: no es el estado, por eso vive en esta columna
                        junto a los contadores y no en el badge de Estado. */}
                    {productionInfo.statusText && (
                        <div className="mb-1"><StatusBadge status={productionInfo.statusText} /></div>
                    )}
                    <div className="flex items-center justify-center gap-4">
                        {sheet.isPrinting && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-gray-500">{twoSided ? 'Impr. Frente' : 'Impresas'}</span>
                                <span><span className="font-bold text-lg">{printedCount}</span><span className="text-gray-500">/{repetitions}</span></span>
                                <button onClick={() => handleProductionUpdate('print')} disabled={printedCount >= repetitions} className="p-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50" title={twoSided ? 'Marcar impresa (frente)' : 'Marcar impresa'}><Printer size={16}/></button>
                            </div>
                        )}
                        {twoSided && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-gray-500">Impr. Dorso</span>
                                <span><span className="font-bold text-lg">{printedCountBack}</span><span className="text-gray-500">/{repetitions}</span></span>
                                <button onClick={() => handleProductionUpdate('printBack')} disabled={printedCountBack >= repetitions} className="p-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50" title="Marcar impresa (dorso)"><Printer size={16}/></button>
                            </div>
                        )}
                        {hasCut && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-gray-500">Cortadas</span>
                                <span><span className="font-bold text-lg">{cutCount}</span><span className="text-gray-500">/{repetitions}</span></span>
                                <button onClick={() => handleProductionUpdate('cut')} disabled={cutCount >= repetitions || (sheet.isPrinting && cutCount >= printedDone(sheet))} className="p-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 disabled:opacity-50" title={sheet.isPrinting && cutCount >= printedDone(sheet) ? 'Imprime ambas caras de más planchas para poder cortar' : 'Marcar cortada'}><Scissors size={16}/></button>
                            </div>
                        )}
                        <button onClick={() => { setFailureModalType('production'); setIsFailureModalOpen(true); }} className="p-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200" title="Reportar Falla"><AlertTriangle size={16}/></button>
                    </div>
                </td>
                <td className="px-2 py-2 text-center align-middle">
                    <div className="flex items-center justify-center gap-4">
                        <button onClick={() => onSelectSheetForDetails(sheet)} title="Gestionar" className="text-gray-400 hover:text-blue-600"><Workflow size={18}/></button>
                        <button onClick={() => onSelectSheetForLog(sheet.history)} title="Ver Historial" className="text-gray-400 hover:text-gray-700"><FileText size={18}/></button>
                        {/* Botón Devolver a Diseño → abre el modal con asignación obligatoria */}
                        {['Listo para Prepress', 'Corregido por Diseño', 'Listo para Producción', 'En Ruta', 'Devuelto a Revisión'].includes(sheet.status) && (
                            <button
                                onClick={() => { setFailureModalType('design'); setIsFailureModalOpen(true); }}
                                title="Devolver a Diseño"
                                className="text-gray-400 hover:text-orange-600"
                            >
                                <CornerUpLeft size={18}/>
                            </button>
                        )}
                    </div>
                </td>
            </tr>
        </>
    );
};

// --- Componente Principal ---
const PrepressTab = ({ sheets, db, storage, jobData, materialsCatalog, user, teamMembers = [], tasks = [] }) => {
    const [sheetForDetails, setSheetForDetails] = useState(null);
    const [sheetForLog, setSheetForLog] = useState(null);
    const [viewerImage, setViewerImage] = useState(null);
    const [statusFilter, setStatusFilter] = useState('Todos');
    const [onlyUpdated, setOnlyUpdated] = useState(false);
    const [userFilter, setUserFilter] = useState('Todos los Miembros');
    const [materialFilter, setMaterialFilter] = useState('Todos los Materiales');
    const [printTypeFilter, setPrintTypeFilter] = useState('Todos');
    const [fileFilter, setFileFilter] = useState('Todas');   // archivos de producción
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'fileName', direction: 'ascending' });
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);

    // Mapa sheetId -> tarea de alerta pendiente. El estado de la alerta manda
    // sobre "Producido" (resuelve la incoherencia) y pinta la fila roja.
    const alertTaskBySheet = useMemo(() => buildAlertTaskBySheet(tasks), [tasks]);

    // Lista COMPLETA de usuarios del sistema para la asignación (cualquier usuario).
    const [allUsers, setAllUsers] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(collection(db, 'users'));
                if (!cancelled) setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error('Error cargando usuarios para asignación:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [db]);

    const uniqueUsers = useMemo(() => {
        const users = new Map();
        (teamMembers || []).forEach(member => {
            if (member.name) {
                users.set(member.name, member.name);
            }
        });
        return ['Todos los Miembros', ...Array.from(users.keys())];
    }, [teamMembers]);
    
    const uniqueMaterials = useMemo(() => {
        if (!materialsCatalog) return [];
        const materialNames = new Set(materialsCatalog.map(m => m.name));
        return ['Todos los Materiales', ...Array.from(materialNames).sort()];
    }, [materialsCatalog]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') { direction = 'descending'; }
        setSortConfig({ key, direction });
    };

    // Se separa en dos pasos A PROPÓSITO: acá se aplica TODO menos el filtro de
    // archivos, para que el conteo de cada opción ("Incompletas (12)") salga sobre
    // lo que dejan los demás filtros. Si se contara después de aplicarlo, elegir
    // una opción derrumbaría las otras a 0 y el conteo dejaría de informar.
    const sheetsAntesDeArchivos = useMemo(() => {
        if (!sheets || !jobData || !materialsCatalog) return [];

        const prepressRelevantSheets = sheets.filter(sheet => PREPRESS_STATUS_WHITELIST.includes(sheet.status));

        let filtered = [...prepressRelevantSheets];

        // El ESTADO es excluyente: comparación directa contra el estado mostrado, que
        // ya no lee `productionStatus` (ver sheetStatus.js). Las opciones del select
        // SON los estados, así que no hace falta ningún mapa intermedio.
        if (statusFilter !== 'Todos') {
            filtered = filtered.filter(s => getSheetDisplayStatus(s, alertTaskBySheet.get(s.id)) === statusFilter);
        }

        // "Actualizada" es ORTOGONAL: se combina con el estado, no lo reemplaza. Una
        // plancha puede estar Lista para Producción Y haber sido actualizada.
        if (onlyUpdated) {
            filtered = filtered.filter(isSheetUpdated);
        }

        if (userFilter !== 'Todos los Miembros') {
            filtered = filtered.filter(s => s.createdByName === userFilter);
        }

        if (materialFilter !== 'Todos los Materiales') {
            const materialId = materialsCatalog.find(m => m.name === materialFilter)?.id;
            if (materialId) {
                filtered = filtered.filter(s => s.materialId === materialId);
            }
        }

        if (printTypeFilter !== 'Todos') {
            filtered = filtered.filter(s => getPrintType(s) === printTypeFilter);
        }

        if (searchTerm.trim()) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(sheet => {
                const materialName = materialsCatalog.find(m => m.id === sheet.materialId)?.name || '';
                const searchableString = `${generateSheetFileName(sheet, jobData)} ${materialName} ${getPrintType(sheet)}`;
                return searchableString.toLowerCase().includes(lowercasedSearch);
            });
        }
        
        return filtered;
    // `alertTaskBySheet` vuelve a las dependencias: el filtro compara contra el estado
    // mostrado, y la alerta tiene prioridad 0 sobre ese estado.
    }, [sheets, statusFilter, onlyUpdated, userFilter, materialFilter, printTypeFilter, searchTerm, jobData, materialsCatalog, alertTaskBySheet]);

    // Conteos de las opciones del filtro de archivos. "En rojo" es un SUBCONJUNTO
    // de "Incompletas", así que los números no suman el total: es deliberado.
    const fileCounts = useMemo(() => tallyReadiness(sheetsAntesDeArchivos), [sheetsAntesDeArchivos]);

    const sortedAndFilteredSheets = useMemo(() => {
        const filtered = sheetsAntesDeArchivos.filter(s => matchesFileFilter(s, fileFilter));

        return [...filtered].sort((a, b) => {
            let aValue, bValue;
            switch (sortConfig.key) {
                case 'material':
                    aValue = materialsCatalog.find(m => m.id === a.materialId)?.name || 'Z';
                    bValue = materialsCatalog.find(m => m.id === b.materialId)?.name || 'Z';
                    break;
                case 'tipo':
                    aValue = getPrintType(a);
                    bValue = getPrintType(b);
                    break;
                case 'estado':
                    aValue = getSheetDisplayStatus(a, alertTaskBySheet.get(a.id));
                    bValue = getSheetDisplayStatus(b, alertTaskBySheet.get(b.id));
                    break;
                case 'fileName':
                default:
                    aValue = generateSheetFileName(a, jobData);
                    bValue = generateSheetFileName(b, jobData);
                    break;
            }
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
    }, [sheetsAntesDeArchivos, fileFilter, sortConfig, jobData, materialsCatalog, alertTaskBySheet]);
    
    return (
        <div className="p-4 bg-gray-100 h-full flex flex-col">
            <ImageViewerModal imageUrl={viewerImage} onClose={() => setViewerImage(null)} />
            <LogModal isOpen={!!sheetForLog} onClose={() => setSheetForLog(null)} history={sheetForLog} />
            <AiAssistantModal isOpen={isAiModalOpen} onClose={() => setIsAiModalOpen(false)} sheetsData={sortedAndFilteredSheets} jobData={jobData} />
            {sheetForDetails && <SheetDetailModal isOpen={true} onClose={() => setSheetForDetails(null)} sheet={sheetForDetails} materialName={materialsCatalog.find(m => m.id === sheetForDetails.materialId)?.name} db={db} storage={storage} jobData={jobData} user={user} allowProductionStatus={true} />}

            <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                <h2 className="text-xl font-bold">Gestión de Prepress</h2>
                <div className="flex items-center gap-2 flex-wrap bg-white p-2 rounded-lg border">
                    <button onClick={() => setIsAiModalOpen(true)} className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center gap-2 text-sm font-semibold"><Sparkles size={16}/> Asistente AI</button>
                    <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 w-48 border rounded-lg text-sm"/></div>
                    <select value={materialFilter} onChange={e => setMaterialFilter(e.target.value)} className="p-2 border rounded-lg text-sm bg-white">
                        {uniqueMaterials.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select value={printTypeFilter} onChange={e => setPrintTypeFilter(e.target.value)} className="p-2 border rounded-lg text-sm bg-white">
                        <option value="Todos">Toda Impresión</option>
                        <option value="Solo Corte">Solo Corte</option>
                        <option value="1 Cara">1 Cara</option>
                        <option value="2 Caras">2 Caras</option>
                    </select>
                    <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="p-2 border rounded-lg text-sm bg-white">
                        {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    {/* Archivos de producción. Los conteos salen de los OTROS filtros activos, así
                        que responden "¿qué falta para producir lo que estoy viendo?" sin seleccionar
                        nada. "En rojo" está contenido en "Incompletas": no suman el total. */}
                    <select
                        value={fileFilter}
                        onChange={e => setFileFilter(e.target.value)}
                        title="Archivos de producción: PRINT (y DORSO en 2 caras) + al menos un archivo de corte"
                        className={`p-2 border rounded-lg text-sm ${fileFilter === 'En rojo' ? 'bg-red-50 border-red-300 text-red-800 font-semibold' : fileFilter === 'Incompletas' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white'}`}
                    >
                        <option value="Todas">Archivos: todos ({fileCounts.todas})</option>
                        <option value="Incompletas">Incompletas ({fileCounts.incompletas})</option>
                        <option value="En rojo">⚠ En rojo ({fileCounts.rojo})</option>
                        <option value="Completas">Completas ({fileCounts.completas})</option>
                    </select>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="p-2 border rounded-lg text-sm bg-white">
                        <option value="Todos">Todos los Estados</option>
                        {SHEET_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {/* "Actualizada" NO es un estado: es un evento ortogonal, se AND-ea con
                        el filtro de arriba. Por eso es un check aparte y no una opción más. */}
                    <label
                        className={`flex items-center gap-2 p-2 border rounded-lg text-sm cursor-pointer ${onlyUpdated ? 'bg-purple-50 border-purple-300 text-purple-800 font-semibold' : 'bg-white'}`}
                        title="Planchas con 'Plancha Actualizada' o 'Plancha Corregida' en su historial"
                    >
                        <input type="checkbox" checked={onlyUpdated} onChange={e => setOnlyUpdated(e.target.checked)} className="accent-purple-600" />
                        Solo actualizadas
                    </label>
                </div>
            </div>

            <div className="flex-grow overflow-auto border rounded-lg bg-white">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[5%]">Preview</th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[15%]"><button onClick={() => requestSort('fileName')} className="flex items-center justify-center gap-1 w-full">Plancha <ArrowUpDown size={12} /></button></th>
                            <th className="px-2 py-3 text-left font-medium text-gray-600 uppercase w-[12%]"><button onClick={() => requestSort('material')} className="flex items-center justify-start gap-1 w-full">Material <ArrowUpDown size={12} /></button></th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[8%]"><button onClick={() => requestSort('tipo')} className="flex items-center justify-center gap-1 w-full">Tipo <ArrowUpDown size={12} /></button></th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[5%]">Reps</th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[15%] min-w-[200px]">Notas</th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[8%] min-w-[110px]" title="Archivos de producción: PRINT (y DORSO en 2 caras) + al menos un archivo de corte. DXF vive solo en el modal.">Archivos</th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[10%]"><button onClick={() => requestSort('estado')} className="flex items-center justify-center gap-1 w-full">Estado <ArrowUpDown size={12} /></button></th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[15%]">Control Producción</th>
                            <th className="px-2 py-3 text-center font-medium text-gray-600 uppercase w-[5%]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {sortedAndFilteredSheets.length > 0 ? (
                            sortedAndFilteredSheets.map(sheet => (
                                <PrepressTableRow
                                    key={sheet.id} sheet={sheet} db={db} jobData={jobData} materialsCatalog={materialsCatalog} user={user}
                                    setViewerImage={setViewerImage} onSelectSheetForDetails={setSheetForDetails}
                                    onSelectSheetForLog={() => setSheetForLog(sheet.history || [])}
                                    users={allUsers}
                                    alertTask={alertTaskBySheet.get(sheet.id) || null}
                                />
                            ))
                        ) : (
                            <tr><td colSpan="10" className="text-center py-10 text-gray-500">No hay planchas que coincidan con los filtros aplicados.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PrepressTab;

